/**
 * Client-side processing of the Gebäudefoto (field mapping #12, GEN-701 QA
 * feedback #2). The bundled print template (`steckbrief.html.hbs`) places the
 * image as-is and expects the design's rounded corners and diagonal cut
 * PRE-APPLIED — its renderer (Flying Saucer, CSS 2.1) has no `clip-path` and
 * does not clip an `<img>` to its `border-radius`. Since GEN-778 the photo is a
 * capture of the building map ("Save map image", the PV Calculator's shared
 * `MapSnapshotButton`) instead of a file upload. Pipeline:
 *
 *   map canvas → center-crop to the photo block's 259.8:166 aspect → downscale
 *   (longest edge ≤ 1200 px) → rounded-corner + diagonal mask → JPEG (quality
 *   0.8) data URI.
 *
 * The map container is sized at {@link STECKBRIEF_PHOTO_ASPECT}, so the crop
 * is a no-op in practice — what the user frames on the map is what prints.
 *
 * The mask replicates the source design's photo block
 * (`steckbrief_v4_ohne-markierung-kosten.html`): a box with `border-radius:
 * 16px; overflow: hidden; background: var(--greige)` holding the image clipped
 * to `clip-path: polygon(0 0, 100% 0, 100% 100%, 22% 100%, 0 78%)`. All four
 * box corners are rounded, and the diagonal cut shows the box's greige, not the
 * page. JPEG has no alpha channel, so the area outside the rounded box is filled
 * with the print's page background (the template's `html, body` background
 * `#F6F5F0`), which prints the same as a transparent corner.
 *
 * Deliberate trade-off (vs a future upload endpoint): the data URI IS the
 * `buildingPhoto` value — it travels in `CreateVariantRequest.values` and is
 * persisted verbatim inside the Variant's jsonb data stamp, stamped
 * `USER_PROVIDED` server-side. No backend upload/storage endpoint exists yet;
 * once one does, this value becomes a storage reference and the pipeline here
 * shrinks to plain upload. Until then {@link STECKBRIEF_PHOTO_MAX_BYTES} keeps
 * the jsonb stamp (and every /calculate round-trip) at a sane size.
 */

/**
 * Aspect ratio of the template's photo block — layout v4 (GEN-775): 259.8 × 166
 * source px (was 290:136 before v4; see the Gebäudefoto note in steckbrief.html.hbs).
 */
export const STECKBRIEF_PHOTO_ASPECT = 259.8 / 166;

/** Longest output edge; with the landscape v4 aspect this is always the width. */
export const STECKBRIEF_PHOTO_MAX_EDGE_PX = 1200;

export const STECKBRIEF_PHOTO_JPEG_QUALITY = 0.8;

/** Reject processed photos above this — the data URI lands in the jsonb stamp. */
export const STECKBRIEF_PHOTO_MAX_BYTES = 500 * 1024;

/** The print's page background — fills the area outside the rounded box (JPEG has no alpha). */
export const STECKBRIEF_PHOTO_PAGE_FILL = '#F6F5F0';

/** The design's photo box background (`--greige`) — shows through the diagonal cut. */
export const STECKBRIEF_PHOTO_CUT_FILL = '#F3F2EA';

/**
 * Corner radius of the photo box as a fraction of its width: the design's
 * `border-radius: 16px` on the 259.8px block (printed 15.5px on 251.6px).
 */
export const STECKBRIEF_PHOTO_CORNER_RADIUS = 16 / 259.8;

/**
 * The diagonal cut of the design as width/height fractions — verbatim from the
 * source design's `clip-path: polygon(0 0, 100% 0, 100% 100%, 22% 100%, 0 78%)`.
 */
export const STECKBRIEF_PHOTO_MASK_POLYGON: readonly (readonly [number, number])[] = [
    [0, 0],
    [1, 0],
    [1, 1],
    [0.22, 1],
    [0, 0.78],
];

/** The processed photo exceeds {@link STECKBRIEF_PHOTO_MAX_BYTES}. */
export class PhotoTooLargeError extends Error {
    readonly bytes: number;

    constructor(bytes: number) {
        super(`Processed photo is ${bytes} bytes (max ${STECKBRIEF_PHOTO_MAX_BYTES})`);
        this.name = 'PhotoTooLargeError';
        this.bytes = bytes;
    }
}

export interface PhotoCropAndTarget {
    /** Source crop rect (center crop to the 259.8:166 block aspect). */
    sx: number;
    sy: number;
    sw: number;
    sh: number;
    /** Output canvas size (crop downscaled to the max edge, never upscaled). */
    targetWidth: number;
    targetHeight: number;
}

/** Pure geometry of the pipeline — exported for tests. */
export function computePhotoCropAndTarget(naturalWidth: number, naturalHeight: number): PhotoCropAndTarget {
    if (naturalWidth <= 0 || naturalHeight <= 0) {
        throw new Error(`Invalid image dimensions ${naturalWidth}x${naturalHeight}`);
    }
    let sx = 0;
    let sy = 0;
    let sw = naturalWidth;
    let sh = naturalHeight;
    if (naturalWidth / naturalHeight > STECKBRIEF_PHOTO_ASPECT) {
        // Wider than the block: crop the sides.
        sw = naturalHeight * STECKBRIEF_PHOTO_ASPECT;
        sx = (naturalWidth - sw) / 2;
    } else {
        // Taller than the block: crop top/bottom.
        sh = naturalWidth / STECKBRIEF_PHOTO_ASPECT;
        sy = (naturalHeight - sh) / 2;
    }
    const targetWidth = Math.max(1, Math.round(Math.min(STECKBRIEF_PHOTO_MAX_EDGE_PX, sw)));
    const targetHeight = Math.max(1, Math.round(targetWidth / STECKBRIEF_PHOTO_ASPECT));
    return { sx, sy, sw, sh, targetWidth, targetHeight };
}

/**
 * Crops, downscales and masks one image (the captured map canvas) onto a new
 * canvas and returns the JPEG data URI. Takes any `CanvasImageSource` so tests
 * can drive it with a fake image + stubbed canvas.
 */
export function renderMaskedPhoto(image: CanvasImageSource, naturalWidth: number, naturalHeight: number): string {
    const { sx, sy, sw, sh, targetWidth, targetHeight } = computePhotoCropAndTarget(naturalWidth, naturalHeight);
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Canvas 2D context is not available');
    }
    // Fill first: JPEG has no alpha, so outside the rounded box shows the page background.
    ctx.fillStyle = STECKBRIEF_PHOTO_PAGE_FILL;
    ctx.fillRect(0, 0, targetWidth, targetHeight);
    // The rounded photo box, filled with the greige that shows through the cut.
    // Built from arcTo, not roundRect: roundRect is missing in older browsers.
    const radius = STECKBRIEF_PHOTO_CORNER_RADIUS * targetWidth;
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.arcTo(targetWidth, 0, targetWidth, targetHeight, radius);
    ctx.arcTo(targetWidth, targetHeight, 0, targetHeight, radius);
    ctx.arcTo(0, targetHeight, 0, 0, radius);
    ctx.arcTo(0, 0, targetWidth, 0, radius);
    ctx.closePath();
    ctx.clip();
    ctx.fillStyle = STECKBRIEF_PHOTO_CUT_FILL;
    ctx.fillRect(0, 0, targetWidth, targetHeight);
    // The diagonal cut — a second clip, so the image stays inside the rounded box.
    ctx.beginPath();
    STECKBRIEF_PHOTO_MASK_POLYGON.forEach(([fx, fy], index) => {
        const x = fx * targetWidth;
        const y = fy * targetHeight;
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(image, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);
    return canvas.toDataURL('image/jpeg', STECKBRIEF_PHOTO_JPEG_QUALITY);
}

/**
 * The capture pipeline (GEN-778): the map canvas → crop/downscale/mask → size
 * guard → data URI — the `buildingPhoto` value.
 *
 * @throws PhotoTooLargeError when the result exceeds the jsonb-stamp budget.
 */
export function processCapturedPhoto(canvas: HTMLCanvasElement): string {
    const dataUri = renderMaskedPhoto(canvas, canvas.width, canvas.height);
    // Data URIs are pure ASCII — string length == byte size.
    if (dataUri.length > STECKBRIEF_PHOTO_MAX_BYTES) {
        throw new PhotoTooLargeError(dataUri.length);
    }
    return dataUri;
}
